import { z } from "zod";

import { canonicalJson, type JsonValue } from "./canonical.js";
import {
  type Dm2016OperationalReleaseResult,
} from "./dm2016-operational-release.js";
import {
  calculateDm2016FootnoteAwareReferenceBundleWithHashes,
  calculateDm2016FootnoteAwareReferenceCaseWithHashes,
  type HashedDm2016FootnoteAwareReferenceBundleResult,
  type HashedDm2016FootnoteAwareReferenceCalculationResult,
  type Dm2016ReferenceCalculationResult,
  type Dm2016ReferenceRulesetIdentity,
} from "./dm2016-reference.js";

export const DM2016_AUTHORITATIVE_SCHEMA_VERSION = "1.4.0";
export const DM2016_AUTHORITATIVE_ROUTE = "dm2016_lab_recalculation";
export const DM2016_BUNDLE_AUTHORITATIVE_SCHEMA_VERSION = "1.2.0";
export const DM2016_BUNDLE_AUTHORITATIVE_ROUTE = "dm2016_bundle_recalculation";

const identifierSchema = z
  .string()
  .min(1)
  .max(100)
  .regex(/^[A-Za-z0-9][A-Za-z0-9._-]*$/);
const codeSchema = z
  .string()
  .min(1)
  .max(40)
  .regex(/^[A-Za-z][A-Za-z0-9.]*$/);
const decimalSchema = z
  .string()
  .min(1)
  .max(80)
  .regex(/^(?:0|[1-9]\d*)(?:\.\d+)?$/);
const sha256Schema = z.string().regex(/^[a-f0-9]{64}$/);
const planningSchema = z.object({
  level: z.enum(["general", "executive"]),
  valueBasis: z.enum([
    "territorial_gdp",
    "existing_and_design_volumes",
    "farm_gross_saleable_production",
  ]),
}).strict();
const footnoteContextSchema = z.object({
  procurementModel: z.enum(["non_integrated", "integrated"]).optional(),
  tenderBaseProject: z.boolean().optional(),
  publicWorksConcession: z.boolean().optional(),
  planning: planningSchema.optional(),
  variationAbsoluteIncrease: decimalSchema.optional(),
  variationAbsoluteDecrease: decimalSchema.optional(),
  newlyDesignedWorksGrossValue: decimalSchema.optional(),
  collaudoInCorsoDOpera: z.boolean().optional(),
  energyDiagnosisDocumentationAvailable: z.boolean().optional(),
}).strict();

export const dm2016AuthoritativeRequestSchema = z.object({
  schemaVersion: z.literal(DM2016_AUTHORITATIVE_SCHEMA_VERSION),
  requestId: identifierSchema,
  route: z.literal(DM2016_AUTHORITATIVE_ROUTE),
  destinationCode: codeSchema,
  serviceCode: codeSchema,
  valueV: decimalSchema,
  inhabitants: decimalSchema.nullable().optional(),
  multiplier: decimalSchema.optional(),
  planning: planningSchema.optional(),
  procurementModel: z.enum(["non_integrated", "integrated"]).optional(),
  tenderBaseProject: z.boolean().optional(),
  publicWorksConcession: z.boolean().optional(),
  interpretationMode: z.enum([
    "printed_source_strict",
    "professionearchitetto_v0_74",
  ]),
  expectedInputSha256: sha256Schema,
  expectedResultSha256: sha256Schema,
}).strict();

export type Dm2016AuthoritativeRequest = z.infer<
  typeof dm2016AuthoritativeRequestSchema
>;

export const dm2016BundleAuthoritativeRequestSchema = z.object({
  schemaVersion: z.literal(DM2016_BUNDLE_AUTHORITATIVE_SCHEMA_VERSION),
  requestId: identifierSchema,
  route: z.literal(DM2016_BUNDLE_AUTHORITATIVE_ROUTE),
  destinationCode: codeSchema,
  services: z.array(z.object({
    serviceCode: codeSchema,
    valueV: decimalSchema.optional(),
    inhabitants: decimalSchema.nullable().optional(),
    multiplier: decimalSchema.optional(),
  }).strict()).min(1).max(96),
  footnoteContext: footnoteContextSchema,
  interpretationMode: z.enum([
    "printed_source_strict",
    "professionearchitetto_v0_74",
  ]),
  expectedInputSha256: sha256Schema,
  expectedResultSha256: sha256Schema,
}).strict();

export type Dm2016BundleAuthoritativeRequest = z.infer<
  typeof dm2016BundleAuthoritativeRequestSchema
>;

export interface Dm2016AuthoritativeCalculationBinding {
  readonly inputSha256: string;
  readonly resultSha256: string;
  readonly engineVersion: string;
  readonly rulesetVersion: string;
  readonly compensationRounded: string;
}

export interface Dm2016AuthoritativePayload {
  readonly schemaVersion: typeof DM2016_AUTHORITATIVE_SCHEMA_VERSION;
  readonly requestId: string;
  readonly route: typeof DM2016_AUTHORITATIVE_ROUTE;
  readonly status: "recalculated_dm2016_lab";
  readonly issuedAt: string;
  readonly expiresAt: string;
  readonly professionalUseAllowed: true;
  readonly certifiedEquoCompenso: false;
  readonly normalizedInput:
    HashedDm2016FootnoteAwareReferenceCalculationResult["normalizedInput"];
  readonly footnoteResolution:
    HashedDm2016FootnoteAwareReferenceCalculationResult["footnoteResolution"];
  readonly calculation: Dm2016AuthoritativeCalculationBinding;
  readonly trace: Dm2016ReferenceCalculationResult;
  readonly release: {
    readonly gateVersion: string;
    readonly manifestReleaseId: string;
    readonly manifestReleaseVersion: string;
    readonly checkedAsOf: string;
    readonly rulesetStatus: "evidence_verified";
    readonly calculationEnabled: true;
    readonly certificationEnabled: false;
    readonly blockers: readonly string[];
    readonly certificationBlockers: readonly string[];
  };
}

export interface SignedDm2016AuthoritativeRecalculation {
  readonly payload: Dm2016AuthoritativePayload;
  readonly signature: {
    readonly algorithm: "HMAC-SHA256";
    readonly keyId: string;
    readonly value: string;
  };
}

export interface Dm2016BundleAuthoritativePayload {
  readonly schemaVersion: typeof DM2016_BUNDLE_AUTHORITATIVE_SCHEMA_VERSION;
  readonly requestId: string;
  readonly route: typeof DM2016_BUNDLE_AUTHORITATIVE_ROUTE;
  readonly status: "recalculated_dm2016_bundle_lab";
  readonly issuedAt: string;
  readonly expiresAt: string;
  readonly professionalUseAllowed: true;
  readonly certifiedEquoCompenso: false;
  readonly normalizedInput:
    HashedDm2016FootnoteAwareReferenceBundleResult["normalizedInput"];
  readonly footnoteResolution:
    HashedDm2016FootnoteAwareReferenceBundleResult["footnoteResolution"];
  readonly calculation: Dm2016AuthoritativeCalculationBinding;
  readonly traces: HashedDm2016FootnoteAwareReferenceBundleResult["references"];
  readonly compensationSummary:
    HashedDm2016FootnoteAwareReferenceBundleResult["compensationSummary"];
  readonly release: Dm2016AuthoritativePayload["release"];
}

export interface SignedDm2016BundleAuthoritativeRecalculation {
  readonly payload: Dm2016BundleAuthoritativePayload;
  readonly signature: SignedDm2016AuthoritativeRecalculation["signature"];
}

export type Dm2016AuthoritativeErrorCode =
  | "INVALID_REQUEST"
  | "INPUT_HASH_MISMATCH"
  | "RESULT_HASH_MISMATCH"
  | "OPERATIONAL_RELEASE_GATE_CLOSED"
  | "SIGNING_CONFIGURATION_INVALID";

export class Dm2016AuthoritativeError extends Error {
  readonly code: Dm2016AuthoritativeErrorCode;
  readonly httpStatus: 400 | 409 | 503;

  constructor(
    code: Dm2016AuthoritativeErrorCode,
    message: string,
    httpStatus: 400 | 409 | 503,
  ) {
    super(message);
    this.name = "Dm2016AuthoritativeError";
    this.code = code;
    this.httpStatus = httpStatus;
  }
}

function freezeDeep<T>(value: T): Readonly<T> {
  if (value !== null && typeof value === "object" && !Object.isFrozen(value)) {
    Object.freeze(value);
    Object.values(value).forEach((child) => freezeDeep(child));
  }
  return value;
}

function requireOperationalRelease(
  release: Dm2016OperationalReleaseResult,
): NonNullable<Dm2016OperationalReleaseResult["manifest"]> {
  if (!release.ready || !release.calculationEnabled || release.manifest === null) {
    throw new Dm2016AuthoritativeError(
      "OPERATIONAL_RELEASE_GATE_CLOSED",
      `Il gate operativo D.M. 2016 è chiuso: ${release.issues.map((issue) => issue.code).join(", ") || "manifest non abilitato"}.`,
      503,
    );
  }
  return release.manifest;
}

function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
}

function hexToBytes(value: string): Uint8Array | null {
  if (!/^[a-f0-9]{64}$/.test(value)) return null;
  const bytes = new Uint8Array(value.length / 2);
  for (let index = 0; index < value.length; index += 2) {
    bytes[index / 2] = Number.parseInt(value.slice(index, index + 2), 16);
  }
  return bytes;
}

async function importSigningKey(secret: string): Promise<CryptoKey> {
  const bytes = new TextEncoder().encode(secret);
  if (bytes.byteLength < 32) {
    throw new Dm2016AuthoritativeError(
      "SIGNING_CONFIGURATION_INVALID",
      "Il segreto di firma server deve contenere almeno 32 byte.",
      503,
    );
  }
  return globalThis.crypto.subtle.importKey(
    "raw",
    bytes,
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign", "verify"],
  );
}

export async function createSignedDm2016AuthoritativeRecalculation(input: {
  readonly request: unknown;
  readonly z1: unknown;
  readonly z2Matrix: unknown;
  readonly selectionRules: unknown;
  readonly identity: Dm2016ReferenceRulesetIdentity;
  readonly operationalRelease: Dm2016OperationalReleaseResult;
  readonly releaseBlockers: readonly string[];
  readonly signingSecret: string;
  readonly keyId: string;
  readonly issuedAt?: Date;
  readonly ttlSeconds?: number;
}): Promise<SignedDm2016AuthoritativeRecalculation> {
  const releaseManifest = requireOperationalRelease(input.operationalRelease);
  const parsed = dm2016AuthoritativeRequestSchema.safeParse(input.request);
  if (!parsed.success) {
    throw new Dm2016AuthoritativeError(
      "INVALID_REQUEST",
      "La richiesta D.M. 2016 non rispetta il contratto previsto.",
      400,
    );
  }
  if (!identifierSchema.safeParse(input.keyId).success) {
    throw new Dm2016AuthoritativeError(
      "SIGNING_CONFIGURATION_INVALID",
      "L’identificativo della chiave server non è valido.",
      503,
    );
  }
  const ttlSeconds = input.ttlSeconds ?? 300;
  if (!Number.isInteger(ttlSeconds) || ttlSeconds < 30 || ttlSeconds > 900) {
    throw new Dm2016AuthoritativeError(
      "SIGNING_CONFIGURATION_INVALID",
      "La durata del payload firmato non è valida.",
      503,
    );
  }

  const request = parsed.data;
  const result = await calculateDm2016FootnoteAwareReferenceCaseWithHashes({
    z1: input.z1,
    z2Matrix: input.z2Matrix,
    selectionRules: input.selectionRules,
    destinationCode: request.destinationCode,
    serviceCode: request.serviceCode,
    valueV: request.valueV,
    ...(typeof request.inhabitants !== "string"
      ? {}
      : { inhabitants: request.inhabitants }),
    ...(request.multiplier === undefined
      ? {}
      : { multiplier: request.multiplier }),
    footnoteContext: {
      ...(request.planning === undefined ? {} : { planning: request.planning }),
      ...(request.procurementModel === undefined
        ? {}
        : { procurementModel: request.procurementModel }),
      ...(request.tenderBaseProject === undefined
        ? {}
        : { tenderBaseProject: request.tenderBaseProject }),
      ...(request.publicWorksConcession === undefined
        ? {}
        : { publicWorksConcession: request.publicWorksConcession }),
    },
    interpretationMode: request.interpretationMode,
    identity: input.identity,
  });
  if (result.inputSha256 !== request.expectedInputSha256) {
    throw new Dm2016AuthoritativeError(
      "INPUT_HASH_MISMATCH",
      "Gli input ricalcolati dal server non coincidono con quelli del browser.",
      409,
    );
  }
  if (result.resultSha256 !== request.expectedResultSha256) {
    throw new Dm2016AuthoritativeError(
      "RESULT_HASH_MISMATCH",
      "Il risultato ricalcolato dal server non coincide con quello del browser.",
      409,
    );
  }

  const issuedAt = input.issuedAt ?? new Date();
  if (Number.isNaN(issuedAt.getTime())) {
    throw new Dm2016AuthoritativeError(
      "SIGNING_CONFIGURATION_INVALID",
      "La data server del payload non è valida.",
      503,
    );
  }
  const expiresAt = new Date(issuedAt.getTime() + ttlSeconds * 1_000);
  const payload: Dm2016AuthoritativePayload = {
    schemaVersion: DM2016_AUTHORITATIVE_SCHEMA_VERSION,
    requestId: request.requestId,
    route: request.route,
    status: "recalculated_dm2016_lab",
    issuedAt: issuedAt.toISOString(),
    expiresAt: expiresAt.toISOString(),
    professionalUseAllowed: true,
    certifiedEquoCompenso: false,
    normalizedInput: result.normalizedInput,
    footnoteResolution: result.footnoteResolution,
    calculation: {
      inputSha256: result.inputSha256,
      resultSha256: result.resultSha256,
      engineVersion: result.engineVersion,
      rulesetVersion: result.rulesetVersion,
      compensationRounded: result.reference.calculation.compensationRounded,
    },
    trace: result.reference,
    release: {
      gateVersion: releaseManifest.versions.operational_gate,
      manifestReleaseId: releaseManifest.release_id,
      manifestReleaseVersion: releaseManifest.release_version,
      checkedAsOf: releaseManifest.checked_as_of,
      rulesetStatus: "evidence_verified",
      calculationEnabled: true,
      certificationEnabled: false,
      blockers: Object.freeze([]),
      certificationBlockers: Object.freeze([
        "AUTOMATIC_LEGAL_CERTIFICATION_DISABLED",
        ...input.releaseBlockers,
      ]),
    },
  };
  const key = await importSigningKey(input.signingSecret);
  const message = new TextEncoder().encode(
    canonicalJson(payload as unknown as JsonValue),
  );
  const signature = new Uint8Array(
    await globalThis.crypto.subtle.sign("HMAC", key, message),
  );
  const document: SignedDm2016AuthoritativeRecalculation = {
    payload,
    signature: {
      algorithm: "HMAC-SHA256",
      keyId: input.keyId,
      value: bytesToHex(signature),
    },
  };
  return freezeDeep(document);
}

export async function createSignedDm2016BundleAuthoritativeRecalculation(input: {
  readonly request: unknown;
  readonly z1: unknown;
  readonly z2Matrix: unknown;
  readonly selectionRules: unknown;
  readonly identity: Dm2016ReferenceRulesetIdentity;
  readonly operationalRelease: Dm2016OperationalReleaseResult;
  readonly releaseBlockers: readonly string[];
  readonly signingSecret: string;
  readonly keyId: string;
  readonly issuedAt?: Date;
  readonly ttlSeconds?: number;
}): Promise<SignedDm2016BundleAuthoritativeRecalculation> {
  const releaseManifest = requireOperationalRelease(input.operationalRelease);
  const parsed = dm2016BundleAuthoritativeRequestSchema.safeParse(input.request);
  if (!parsed.success) {
    throw new Dm2016AuthoritativeError(
      "INVALID_REQUEST",
      "La richiesta multi-prestazione D.M. 2016 non rispetta il contratto previsto.",
      400,
    );
  }
  if (!identifierSchema.safeParse(input.keyId).success) {
    throw new Dm2016AuthoritativeError(
      "SIGNING_CONFIGURATION_INVALID",
      "L’identificativo della chiave server non è valido.",
      503,
    );
  }
  const ttlSeconds = input.ttlSeconds ?? 300;
  if (!Number.isInteger(ttlSeconds) || ttlSeconds < 30 || ttlSeconds > 900) {
    throw new Dm2016AuthoritativeError(
      "SIGNING_CONFIGURATION_INVALID",
      "La durata del payload firmato non è valida.",
      503,
    );
  }

  const request = parsed.data;
  const result = await calculateDm2016FootnoteAwareReferenceBundleWithHashes({
    z1: input.z1,
    z2Matrix: input.z2Matrix,
    selectionRules: input.selectionRules,
    destinationCode: request.destinationCode,
    services: request.services.map((service) => ({
      serviceCode: service.serviceCode,
      ...(service.valueV === undefined ? {} : { valueV: service.valueV }),
      ...(typeof service.inhabitants !== "string"
        ? {}
        : { inhabitants: service.inhabitants }),
      ...(service.multiplier === undefined
        ? {}
        : { multiplier: service.multiplier }),
    })),
    footnoteContext: {
      ...(request.footnoteContext.procurementModel === undefined
        ? {}
        : { procurementModel: request.footnoteContext.procurementModel }),
      ...(request.footnoteContext.tenderBaseProject === undefined
        ? {}
        : { tenderBaseProject: request.footnoteContext.tenderBaseProject }),
      ...(request.footnoteContext.publicWorksConcession === undefined
        ? {}
        : { publicWorksConcession: request.footnoteContext.publicWorksConcession }),
      ...(request.footnoteContext.planning === undefined
        ? {}
        : { planning: request.footnoteContext.planning }),
      ...(request.footnoteContext.variationAbsoluteIncrease === undefined
        ? {}
        : { variationAbsoluteIncrease: request.footnoteContext.variationAbsoluteIncrease }),
      ...(request.footnoteContext.variationAbsoluteDecrease === undefined
        ? {}
        : { variationAbsoluteDecrease: request.footnoteContext.variationAbsoluteDecrease }),
      ...(request.footnoteContext.newlyDesignedWorksGrossValue === undefined
        ? {}
        : { newlyDesignedWorksGrossValue: request.footnoteContext.newlyDesignedWorksGrossValue }),
      ...(request.footnoteContext.collaudoInCorsoDOpera === undefined
        ? {}
        : { collaudoInCorsoDOpera: request.footnoteContext.collaudoInCorsoDOpera }),
      ...(request.footnoteContext.energyDiagnosisDocumentationAvailable === undefined
        ? {}
        : {
          energyDiagnosisDocumentationAvailable:
            request.footnoteContext.energyDiagnosisDocumentationAvailable,
        }),
    },
    interpretationMode: request.interpretationMode,
    identity: input.identity,
  });
  if (result.inputSha256 !== request.expectedInputSha256) {
    throw new Dm2016AuthoritativeError(
      "INPUT_HASH_MISMATCH",
      "Gli input multi-prestazione ricalcolati dal server non coincidono con quelli del browser.",
      409,
    );
  }
  if (result.resultSha256 !== request.expectedResultSha256) {
    throw new Dm2016AuthoritativeError(
      "RESULT_HASH_MISMATCH",
      "Il risultato multi-prestazione ricalcolato dal server non coincide con quello del browser.",
      409,
    );
  }

  const issuedAt = input.issuedAt ?? new Date();
  if (Number.isNaN(issuedAt.getTime())) {
    throw new Dm2016AuthoritativeError(
      "SIGNING_CONFIGURATION_INVALID",
      "La data server del payload non è valida.",
      503,
    );
  }
  const expiresAt = new Date(issuedAt.getTime() + ttlSeconds * 1_000);
  const payload: Dm2016BundleAuthoritativePayload = {
    schemaVersion: DM2016_BUNDLE_AUTHORITATIVE_SCHEMA_VERSION,
    requestId: request.requestId,
    route: request.route,
    status: "recalculated_dm2016_bundle_lab",
    issuedAt: issuedAt.toISOString(),
    expiresAt: expiresAt.toISOString(),
    professionalUseAllowed: true,
    certifiedEquoCompenso: false,
    normalizedInput: result.normalizedInput,
    footnoteResolution: result.footnoteResolution,
    calculation: {
      inputSha256: result.inputSha256,
      resultSha256: result.resultSha256,
      engineVersion: result.engineVersion,
      rulesetVersion: result.rulesetVersion,
      compensationRounded: result.compensationSummary.compensationRounded,
    },
    traces: result.references,
    compensationSummary: result.compensationSummary,
    release: {
      gateVersion: releaseManifest.versions.operational_gate,
      manifestReleaseId: releaseManifest.release_id,
      manifestReleaseVersion: releaseManifest.release_version,
      checkedAsOf: releaseManifest.checked_as_of,
      rulesetStatus: "evidence_verified",
      calculationEnabled: true,
      certificationEnabled: false,
      blockers: Object.freeze([]),
      certificationBlockers: Object.freeze([
        "AUTOMATIC_LEGAL_CERTIFICATION_DISABLED",
        ...input.releaseBlockers,
      ]),
    },
  };
  const key = await importSigningKey(input.signingSecret);
  const message = new TextEncoder().encode(
    canonicalJson(payload as unknown as JsonValue),
  );
  const signature = new Uint8Array(
    await globalThis.crypto.subtle.sign("HMAC", key, message),
  );
  const document: SignedDm2016BundleAuthoritativeRecalculation = {
    payload,
    signature: {
      algorithm: "HMAC-SHA256",
      keyId: input.keyId,
      value: bytesToHex(signature),
    },
  };
  return freezeDeep(document);
}

export async function verifySignedDm2016AuthoritativeRecalculation(input: {
  readonly document: SignedDm2016AuthoritativeRecalculation;
  readonly signingSecret: string;
  readonly at?: Date;
}): Promise<boolean> {
  const signature = hexToBytes(input.document.signature.value);
  if (
    signature === null
    || input.document.signature.algorithm !== "HMAC-SHA256"
  ) return false;
  const at = input.at ?? new Date();
  const issuedAt = Date.parse(input.document.payload.issuedAt);
  const expiresAt = Date.parse(input.document.payload.expiresAt);
  if (
    Number.isNaN(at.getTime())
    || Number.isNaN(issuedAt)
    || Number.isNaN(expiresAt)
    || at.getTime() < issuedAt
    || at.getTime() > expiresAt
  ) return false;

  try {
    const key = await importSigningKey(input.signingSecret);
    const message = new TextEncoder().encode(
      canonicalJson(input.document.payload as unknown as JsonValue),
    );
    const signatureBuffer = new ArrayBuffer(signature.byteLength);
    new Uint8Array(signatureBuffer).set(signature);
    return globalThis.crypto.subtle.verify(
      "HMAC",
      key,
      signatureBuffer,
      message,
    );
  } catch {
    return false;
  }
}

export async function verifySignedDm2016BundleAuthoritativeRecalculation(input: {
  readonly document: SignedDm2016BundleAuthoritativeRecalculation;
  readonly signingSecret: string;
  readonly at?: Date;
}): Promise<boolean> {
  const signature = hexToBytes(input.document.signature.value);
  if (
    signature === null
    || input.document.signature.algorithm !== "HMAC-SHA256"
  ) return false;
  const at = input.at ?? new Date();
  const issuedAt = Date.parse(input.document.payload.issuedAt);
  const expiresAt = Date.parse(input.document.payload.expiresAt);
  if (
    Number.isNaN(at.getTime())
    || Number.isNaN(issuedAt)
    || Number.isNaN(expiresAt)
    || at.getTime() < issuedAt
    || at.getTime() > expiresAt
  ) return false;

  try {
    const key = await importSigningKey(input.signingSecret);
    const message = new TextEncoder().encode(
      canonicalJson(input.document.payload as unknown as JsonValue),
    );
    const signatureBuffer = new ArrayBuffer(signature.byteLength);
    new Uint8Array(signatureBuffer).set(signature);
    return globalThis.crypto.subtle.verify(
      "HMAC",
      key,
      signatureBuffer,
      message,
    );
  } catch {
    return false;
  }
}
